/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';
import {StateDeclarationBuilder, AdnRouterHelperApi} from '../routing/state-utils';
import _ from 'lodash';
import {OBJECT_TYPE} from "../common/constants/object-type";
import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {LibraryCreativeListContextComponent} from './library-creative-list-context.component';
import {CreativeBulkUploadPageComponent} from '../creative/creative-bulk-upload-page.component';
import {LibraryCreativeContextActionsComponent} from './library-creative-context-actions.component';

import {stateTitleView} from '../common/directives/html/state-title.component';
import {LibraryCreativeApiService, LibraryCreativeModel} from './library-creative-api.service';
import {LibraryCreativeEditPageComponent} from './library-creative-edit-page.component';
import {LibraryCreativeListPageComponent} from './library-creative-list-page.component';

export function libraryCreativeStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
const api = adnRouterHelper.services.service(LibraryCreativeApiService);
const permissions = adnRouterHelper.services.localUserPermissions();
const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
  && !permissions.hasAny(PERMISSIONS.axAdvertiser.apiKey)
  && !permissions.hasAny(PERMISSIONS.creativeAgency.apiKey);
let rh = adnRouterHelper.createRouterHelper('library-creatives.library-creative', LibraryCreativeApiService, 'app.adv', 'app');
    
        let paramsExtras = {modelId: '', modelName: '', fromLineItem: false};
        states.add(rh.listPath(), rh.list({
          resolve: {
            sectionParam: function() {
              return 'creatives';
            }
          },
          views: _.merge({}, {
            'content@app': {component: LibraryCreativeListPageComponent, bindings: {readOnly: 'listReadOnly'}},
            'context@app': {
              component: LibraryCreativeListContextComponent,
              bindings: {readOnly: 'listReadOnly'}
            }
          })
        }))
          .add(rh.newPath('bulkupload'), rh.item({
            url: '/bulk-upload',
            resolve: {
              model: function() {
                return null;
              },
              creativeType: function() {
                return 'LIBRARY_CREATIVE';
              }
            },
            views: {
              'content@app': {
                component: CreativeBulkUploadPageComponent,
                bindings: {creativeType: 'creativeType'}
              },
              'title@app': stateTitleView()
            },
            data: {
              title: 'Library Creative Bulk Upload'
            }
          }))
          .add(rh.newItemPath(), rh.newItem(paramsExtras))
          .add(rh.itemPath(), rh.item({
            params: paramsExtras,
            resolve: {
              creativeType: function() {
                return OBJECT_TYPE.LibraryCreative;
              },
              lineItemInfo: function() {
                return {};
              },
              libraryCreativePage: function($stateParams) {
                if ($stateParams.isNew) {
                  const model: LibraryCreativeModel = {
                    name: "",
                    id: $stateParams.id,
                    assets: [],
                    objectState: 'ACTIVE',
                    validationWarnings: []
                  };
                  return new SignalAdminResourcePageState(
                    model, true, readOnly(), api, 'Library Creative', 'name', undefined, undefined,
                    {objectTypeKey: 'LibraryCreative', crumbParams: []}
                  );
                }
                return firstValueFrom(api.getHydrated($stateParams.id).pipe(
                  tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
                )).then((model) => new SignalAdminResourcePageState(
                  model, false, readOnly(), api, 'Library Creative', 'name', undefined, undefined,
                  {objectTypeKey: 'LibraryCreative', crumbParams: []}
                ));
              },
              model: (libraryCreativePage: SignalAdminResourcePageState<LibraryCreativeModel>) => libraryCreativePage.model()
            },
            views: {
              'content@app': {
                component: LibraryCreativeEditPageComponent,
                bindings: {state: 'libraryCreativePage', creativeType: 'creativeType', lineItemInfo: 'lineItemInfo'}
              },
              'context@app': {
                component: LibraryCreativeContextActionsComponent,
                bindings: {state: 'libraryCreativePage'}
              },
              'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'libraryCreativePage'}}
            }
          }));
  return states.build();
}
